import { useCallback, useEffect, useRef, useState } from "react";

import type {
  SimulationCommand,
  SimulationEvent,
  SimulationState,
} from "../simulation/messages";
import type { WorldState } from "../simulation/world";

const initialState: SimulationState = {
  continentCount: 0,
  population: 0,
  resourceNodeCount: 0,
  sample: 0,
  seed: 0x1f1ef1a5,
  simulationSeconds: 0,
  speed: 1,
  status: "starting",
  tick: 0,
  worldChecksum: "",
};

export function useSimulation() {
  const workerReference = useRef<Worker | null>(null);
  const [state, setState] = useState(initialState);
  const [world, setWorld] = useState<WorldState | null>(null);

  useEffect(() => {
    const worker = new Worker(
      new URL("../simulation/simulation.worker.ts", import.meta.url),
      {
        type: "module",
      },
    );
    workerReference.current = worker;

    worker.onmessage = (message: MessageEvent<SimulationEvent>) => {
      switch (message.data.type) {
        case "state":
          setState(message.data.state);
          return;
        case "world-snapshot":
          setWorld(message.data.world);
          return;
        case "error":
          setState((current) => ({ ...current, status: "error" }));
          console.error(message.data.message);
          return;
      }
    };

    worker.onerror = (event) => {
      setState((current) => ({ ...current, status: "error" }));
      console.error(event.message);
    };

    return () => {
      workerReference.current = null;
      worker.terminate();
    };
  }, []);

  const send = useCallback((command: SimulationCommand) => {
    workerReference.current?.postMessage(command);
  }, []);

  return {
    pause: useCallback(() => send({ type: "pause" }), [send]),
    reset: useCallback(() => send({ type: "reset" }), [send]),
    resume: useCallback(() => send({ type: "resume" }), [send]),
    setSpeed: useCallback(
      (speed: number) => send({ speed, type: "set-speed" }),
      [send],
    ),
    state,
    world,
  };
}
